import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { dashboardAPI, subscriptionAPI } from "../lib/api";
import { formatDate } from "../lib/utils";
import {
  Users,
  MessageSquare,
  Package,
  Calendar,
  TrendingUp,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

export function Dashboard() {
  const [stats, setStats] = useState({
    total_customers: 0,
    total_interactions: 0,
    total_assets: 0,
    active_subscriptions: 0,
    recent_interactions: 0,
  });
  const [upcomingRenewals, setUpcomingRenewals] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsResponse, renewalsResponse] = await Promise.all([
        dashboardAPI.getStats(),
        subscriptionAPI.getUpcomingRenewals(30)
      ]);
      
      setStats(statsResponse.data);
      setUpcomingRenewals(renewalsResponse.data.slice(0, 5)); // Show only first 5
    } catch (error) {
      toast.error("Failed to load dashboard data");
      console.error("Dashboard load error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const statsCards = [
    {
      title: "Total Customers",
      value: stats.total_customers,
      icon: Users,
      color: "text-blue-600",
      bg: "bg-blue-50",
      link: "/customers",
      testId: "total-customers-card"
    },
    {
      title: "Recent Interactions",
      value: stats.recent_interactions,
      icon: MessageSquare,
      color: "text-green-600",
      bg: "bg-green-50",
      link: "/interactions",
      testId: "recent-interactions-card"
    },
    {
      title: "Total Assets",
      value: stats.total_assets,
      icon: Package,
      color: "text-purple-600",
      bg: "bg-purple-50",
      link: "/assets",
      testId: "total-assets-card"
    },
    {
      title: "Active Subscriptions",
      value: stats.active_subscriptions,
      icon: Calendar,
      color: "text-orange-600",
      bg: "bg-orange-50",
      link: "/subscriptions",
      testId: "active-subscriptions-card"
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Overview of your Onsite Technology Solutions CRM</p>
        </div>
        
        <Button
          onClick={loadDashboardData}
          variant="outline"
          className="btn-hover"
          data-testid="refresh-dashboard-button"
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.title} to={stat.link}>
              <Card className="card-hover cursor-pointer" data-testid={stat.testId}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-900 mt-2">
                        {stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 ${stat.bg} rounded-lg flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Upcoming Renewals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
              Upcoming Renewals
            </CardTitle>
            <CardDescription>
              Subscriptions that need renewal attention in the next 30 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            {upcomingRenewals.length > 0 ? (
              <div className="space-y-4" data-testid="upcoming-renewals-list">
                {upcomingRenewals.map((item) => (
                  <div key={item.subscription.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">
                        {item.customer.name}
                      </p>
                      <p className="text-sm text-slate-600">
                        {item.subscription.service_name}
                      </p>
                      <p className="text-xs text-orange-600">
                        Reminder: {formatDate(item.subscription.renewal_reminder_date)}
                      </p>
                    </div>
                    <Link
                      to={`/customers/${item.customer.id}`}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      View →
                    </Link>
                  </div>
                ))}
                <Link to="/subscriptions">
                  <Button variant="outline" className="w-full mt-2">
                    View All Subscriptions
                  </Button>
                </Link>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8" data-testid="no-upcoming-renewals">
                No upcoming renewals in the next 30 days
              </p>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Common tasks to manage your customer relationships
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="quick-actions">
              <Link to="/customers">
                <Button variant="outline" className="w-full justify-start btn-hover">
                  <Users className="w-4 h-4 mr-2" />
                  Add New Customer
                </Button>
              </Link>
              
              <Link to="/interactions">
                <Button variant="outline" className="w-full justify-start btn-hover">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Log Interaction
                </Button>
              </Link>
              
              <Link to="/assets">
                <Button variant="outline" className="w-full justify-start btn-hover">
                  <Package className="w-4 h-4 mr-2" />
                  Register Asset
                </Button>
              </Link>
              
              <Link to="/subscriptions">
                <Button variant="outline" className="w-full justify-start btn-hover">
                  <Calendar className="w-4 h-4 mr-2" />
                  Manage Subscriptions
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
